import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatMoney } from '@/lib/utils';

interface SavingsLineChartProps {
  data: Array<{
    month: string;
    savings: number;
  }>;
  currency: string;
}

// Custom tooltip component (outside main component to avoid render errors)
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: {
      month: string;
      savings: number;
    };
  }>;
  currency: string;
}

const CustomTooltip = ({ active, payload, currency }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl border border-slate-200 dark:border-slate-700 rounded-xl p-3 shadow-xl">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {payload[0].payload.month}
        </p>
        <p className="text-lg font-bold text-blue-600 dark:text-purple-400">
          {formatMoney(payload[0].value, currency)}
        </p>
      </div>
    );
  }
  return null;
};

export function SavingsLineChart({ data, currency }: SavingsLineChartProps) {

  return (
    <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
          <span className="text-xl">📈</span>
        </div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          Savings Trend
        </h3>
      </div>
      
      {data.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-500 dark:text-slate-400">No data available yet</p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
            Start tracking your monthly finances to see trends
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <defs>
              <linearGradient id="savingsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#9333ea" stopOpacity={0.8}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.3} />
            <XAxis 
              dataKey="month" 
              stroke="#64748b" 
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#64748b" 
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => formatMoney(value, currency)}
            />
            <Tooltip content={<CustomTooltip currency={currency} />} />
            <Line 
              type="monotone" 
              dataKey="savings" 
              stroke="url(#savingsGradient)" 
              strokeWidth={3}
              dot={{ fill: '#3b82f6', r: 5 }}
              activeDot={{ r: 7, fill: '#9333ea' }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}